import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { isMobile } from 'react-device-detect';
import Select from 'react-select';
import { Button, Modal } from 'react-bootstrap';

const IFViewSchedulePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const batchId = queryParams.get('batch');
  const programId = queryParams.get('program');

  const LMS = process.env.REACT_APP_API_LMS;
  const TOKEN = process.env.REACT_APP_API_TOKEN;

  // Data & UI States
  const [schedules, setSchedules] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [pastSessions, setPastSessions] = useState([]);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  // Filter States
  const [courseNameFilter, setCourseNameFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');

  // Modal States
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  // Message & Mobile
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Courses dropdown state
  const [courses, setCourses] = useState([]);

  // React-select options for date filter
  const dateFilterOptions = [
    { value: '', label: 'All Dates' },
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: 'current_week', label: 'Current Week' },
  ];

  // Build course options for react-select
  const courseOptions = [
    { value: '', label: 'All Courses' },
    ...courses.map(c => ({ value: c.title, label: c.title }))
  ];

  // Fetch courses by program
  const fetchCoursesByProgram = async () => {
    if (!programId) return;
    try {
      const response = await fetch(`${LMS}/api/courses-by-program`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': TOKEN,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ program_ids: [parseInt(programId, 10)] }),
      });

      if (!response.ok) throw new Error('Failed to fetch courses');

      const result = await response.json();
      if (result.data) {
        setCourses(result.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchCoursesByProgram();
  }, [programId]);

  // ==================== FETCH SCHEDULES ====================
  const fetchSchedules = async (page = 1) => {
    if (!batchId) {
      setMessage({ type: 'warning', text: 'Batch ID not found in URL.' });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    // Build query string for GET request
    const params = new URLSearchParams({
      batch_id: batchId,
      page,
      per_page: 10,
      ...(courseNameFilter && { course_name: courseNameFilter }),
      ...(dateRangeFilter && { date: dateRangeFilter }),
      ...(dateFilter && { filter_date: dateFilter }),
      ...(startDateFilter && { start_date: startDateFilter }),
      ...(endDateFilter && { end_date: endDateFilter }),
    });

    try {
      const response = await fetch(`${LMS}/api/batch-schedule?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': TOKEN,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch schedules');

      const result = await response.json();

      if (result.status === true) {
        if (Array.isArray(result.upcoming_sessions) || Array.isArray(result.past_sessions)) {
          const up = Array.isArray(result.upcoming_sessions) ? result.upcoming_sessions : [];
          const past = Array.isArray(result.past_sessions) ? result.past_sessions : [];
          setUpcomingSessions(up);
          setPastSessions(past);
          setSchedules([...up, ...past]);
          setCurrentPage(1);
          setTotalPages(1);
          setTotalItems(activeTab === 'upcoming' ? up.length : past.length);
        } else {
          const list = result.data?.data || [];
          setSchedules(list);
          const up = list.filter(s => {
            const st = getSessionStatus(s.date);
            return st === 'upcoming' || st === 'today';
          });
          const past = list.filter(s => getSessionStatus(s.date) === 'past');
          setUpcomingSessions(up);
          setPastSessions(past);
          setCurrentPage(result.data?.current_page || 1);
          setTotalPages(result.data?.last_page || 1);
          setTotalItems(activeTab === 'upcoming' ? up.length : past.length);
        }
      } else {
        throw new Error(result.message || 'No data received');
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: 'danger', text: err.message || 'Error loading schedules' });
      setSchedules([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules(currentPage);
  }, [batchId, currentPage, courseNameFilter, dateFilter, dateRangeFilter, startDateFilter, endDateFilter]);

  useEffect(() => {
    let tooltips = [];
    import('bootstrap/dist/js/bootstrap.bundle.min.js')
      .then((bootstrap) => {
        const triggers = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        tooltips = [...triggers].map((t) => new bootstrap.Tooltip(t, { boundary: 'window' }));
      })
      .catch(() => {});
    return () => {
      tooltips.forEach((t) => t.dispose && t.dispose());
    };
  }, [activeTab, upcomingSessions, pastSessions]);

  // ==================== DELETE SCHEDULE ====================
  const confirmDelete = async () => {
    if (!selectedSchedule) return;

    setIsDeleting(true);

    try {
      const response = await fetch(`${LMS}/api/batch-schedule/delete/${selectedSchedule.id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Authorization': TOKEN,
        },
      });

      const result = await response.json();

      if (response.ok && result.status) {
        setMessage({ type: 'success', text: 'Schedule deleted successfully!' });
        fetchSchedules(currentPage);
        setShowDeleteModal(false);
        setSelectedSchedule(null);
      } else {
        throw new Error(result.message || 'Failed to delete');
      }
    } catch (err) {
      setMessage({ type: 'danger', text: err.message });
    } finally {
      setIsDeleting(false);
    }
  };

  const openVideo = (url) => {
    if (!url) return;
    setVideoUrl(url);
    setShowVideoModal(true);
  };

  // ==================== HELPERS ====================
  const formatTime = (time) => {
    if (!time) return '—';
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Determine if the session is upcoming or past
  const getSessionStatus = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sessionDate = new Date(dateStr);
    sessionDate.setHours(0, 0, 0, 0);
    if (sessionDate > today) return 'upcoming';
    if (sessionDate < today) return 'past';
    return 'today';
  };

  const handleReset = () => {
    setCourseNameFilter('');
    setDateFilter('');
    setDateRangeFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
  };

  const handleAddSchedule = () => {
    window.location.href = `/teacher-add-schedule?batch=${batchId}&program=${programId}`;
  };

  const handleEditSchedule = (scheduleId) => {
    window.location.href = `/teacher-edit-schedule?schedule_id=${scheduleId}&batch=${batchId}&program=${programId}`;
  };

  const getRecordingTooltip = (schedule) => {
    if (schedule.recording_file) return 'Play recording';
    if (!schedule.date || !schedule.end_time) return 'Processing';
    const end = new Date(`${schedule.date}T${schedule.end_time}`);
    const diff = Date.now() - end.getTime();
    const threshold = 90 * 60 * 1000;
    if (diff < threshold) return 'Please wait while we process your recording';
    return 'Something went wrong with video recording technical team will fix it asap';
  };

  return (
    <>
      {/* ======== CLEAN PAGE HEADER ======== */}
      <PageHeader
        title="View Schedule"
        breadcrumbs={[
          { label: 'Dashboard', path: '/teacher-dashboard' },
          { label: 'Batches', path: '/teacher-batches' },
          { label: 'View Schedule', active: true },
        ]}
      />

      {/* ======== MESSAGES ======== */}
      {message.text && (
        <div className="row mb-3">
          <div className="col-12">
            <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
              <strong>
                {message.type === 'success' ? '✓ Success!' : 
                 message.type === 'danger' ? '✕ Error!' : 'ℹ Info!'}
              </strong>{' '}
              {message.text}
              <button type="button" className="btn-close" onClick={() => setMessage({type:'',text:''})}></button>
            </div>
          </div>
        </div>
      )}

      {/* ======== FILTERS (Desktop) ======== */}
      <div className={isMobile ? 'border-0 mb-4 rounded-2 mt-3' : 'mb-4 rounded-2 p-md-3 border'}>
        <div className="row mb-1 d-none d-md-flex">
          <div className="col-9">
            <label className="form-label text-lg fw-semibold text-dark">Filter Options</label>
          </div>
          <div className="col-3 d-flex justify-content-end">
            <button className="btn btn-primary" onClick={handleAddSchedule}>
              <i className="fas fa-plus me-2" /> Add Schedule
            </button>
          </div>
        </div>

        <div className="row mb-md-2">
          <div className="col-12">
            <div className="d-md-none">
              <div className="d-grid gap-2">
                <button className="btn btn-primary" onClick={handleAddSchedule}>
                  <i className="fas fa-plus me-2" /> Add New Schedule
                </button>
                <button className="btn btn-outline-primary" onClick={() => setShowMobileFilters(true)}>
                  <i className="fas fa-filter me-2" /> Filters
                  {(courseNameFilter || dateFilter || dateRangeFilter || startDateFilter || endDateFilter) && (
                    <span className="badge bg-danger ms-2">
                      {[courseNameFilter, dateFilter, dateRangeFilter, startDateFilter, endDateFilter].filter(Boolean).length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            <div className="d-none d-md-block">
              <div className="card-body">
                <div className="row g-2 align-items-end">
                  <div className="col-md-2">
                    <label className="form-label fw-semibold text-dark">Course</label>
                    <Select
                      options={courseOptions}
                      value={courseOptions.find(opt => opt.value === courseNameFilter)}
                      onChange={selected => setCourseNameFilter(selected ? selected.value : '')}
                      placeholder="All Courses"
                      isClearable
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label fw-semibold text-dark">Date Filter</label>
                    <Select
                      options={dateFilterOptions}
                      value={dateFilterOptions.find(opt => opt.value === dateFilter)}
                      onChange={selected => setDateFilter(selected ? selected.value : '')}
                      placeholder="All Dates"
                      isClearable
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label fw-semibold text-dark">Specific Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={dateRangeFilter}
                      onChange={e => setDateRangeFilter(e.target.value)}
                      onClick={e => e.target.showPicker && e.target.showPicker()}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label fw-semibold text-dark">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDateFilter}
                      onChange={e => setStartDateFilter(e.target.value)}
                      onClick={e => e.target.showPicker && e.target.showPicker()}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label fw-semibold text-dark">End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={endDateFilter}
                      onChange={e => setEndDateFilter(e.target.value)}
                      onClick={e => e.target.showPicker && e.target.showPicker()}
                    />
                  </div>
                  <div className="col-md-2 d-flex">
                    <button className="btn btn-outline-secondary w-100" onClick={handleReset} disabled={isLoading} title="Reset Filters">
                      <i className="fas fa-sync-alt" /> Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showMobileFilters && (
        <>
          <div 
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-md-none" 
            style={{ zIndex: 1040 }} 
            onClick={() => setShowMobileFilters(false)} 
          />
          <div
            className="position-fixed top-0 end-0 h-100 bg-white shadow-lg d-md-none"
            style={{
              width: '320px',
              zIndex: 1050,
              transform: showMobileFilters ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 0.3s ease-in-out',
              overflowY: 'auto',
            }}
          >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-primary text-white">
              <h5 className="mb-0 text-white">
                <i className="fas fa-filter me-2" /> Filters
              </h5>
              <button 
                className="btn btn-sm btn-outline-light" 
                onClick={() => setShowMobileFilters(false)}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="p-3">
              <div className="mb-3">
                <label className="form-label fw-semibold text-dark">Course</label>
                <Select
                  options={courseOptions}
                  value={courseOptions.find(opt => opt.value === courseNameFilter)}
                  onChange={selected => setCourseNameFilter(selected ? selected.value : '')}
                  placeholder="All Courses"
                  isClearable
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold text-dark">Date Filter</label>
                <Select
                  options={dateFilterOptions}
                  value={dateFilterOptions.find(opt => opt.value === dateFilter)}
                  onChange={selected => setDateFilter(selected ? selected.value : '')}
                  placeholder="All Dates"
                  isClearable
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold text-dark">Specific Date</label>
                <input type="date" className="form-control" value={dateRangeFilter} onChange={e => setDateRangeFilter(e.target.value)} />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold text-dark">Start Date</label>
                <input type="date" className="form-control" value={startDateFilter} onChange={e => setStartDateFilter(e.target.value)} />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold text-dark">End Date</label>
                <input type="date" className="form-control" value={endDateFilter} onChange={e => setEndDateFilter(e.target.value)} />
              </div>
            </div>

            <div className="p-3 border-top mt-auto">
              <div className="d-grid gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    handleReset();
                    setShowMobileFilters(false);
                  }}
                  disabled={isLoading}
                >
                  <i className="fas fa-sync-alt me-2" /> Reset All Filters
                </button>
                <button 
                  className="btn btn-success" 
                  onClick={() => setShowMobileFilters(false)}
                >
                  <i className="fas fa-check me-2" /> Apply Filters
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ======== SCHEDULE TABLE / CARDS ======== */}
      <div className="row">
        <div className="col-12">
          <div className="card skyblueborder">
            <div className="card-header skyblue-header">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start">
                <h5 className="mb-2 mb-md-0 text-primary">
                  <i className="fas fa-calendar-alt me-2"></i>
                  Schedule Management ({activeTab === 'upcoming' ? upcomingSessions.length : pastSessions.length})
                </h5>
              <Button
                variant="link"
                className="p-0 text-decoration-none text-primary fw-semibold"
                onClick={() => navigate('/teacher-batches')}
              >
                <i className="fas fa-arrow-left me-1"></i> Back to Batch List
              </Button>
              </div>
            </div>

            <div className="px-3 pt-3 pb-3">
              <div className="d-flex gap-2">
                <button
                  className={`btn ${activeTab === 'upcoming' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveTab('upcoming')}
                >
                  Upcoming Session
                </button>
                <button
                  className={`btn ${activeTab === 'past' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveTab('past')}
                >
                  Past Session
                </button>
              </div>
            </div>

            {isLoading ? (
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (activeTab === 'upcoming' ? upcomingSessions.length > 0 : pastSessions.length > 0) ? (
              <>
                {/* Desktop Table */}
                <div className="table-responsive d-none d-md-block">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Batch</th>
                        <th>Course Title</th>
                        <th>Date</th>
                        <th>Day</th>
                        <th>Time</th>
                        <th>Status</th>
                        <th>Meet</th>
                        <th>Actions</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(activeTab === 'upcoming' ? upcomingSessions : pastSessions).map((schedule) => {
                        const isPastTab = activeTab === 'past';
                        const sessionStatus = getSessionStatus(schedule.date);
                        const isJoinDisabled = isPastTab ? true : (sessionStatus === 'upcoming');
                        return (
                          <tr key={`${schedule.id}-${schedule.recordingid ?? 'nr'}`}>
                            <td className="fw-semibold">{schedule.batch_name}</td>
                            <td className="fw-medium">{schedule.title}</td>
                            <td>{formatDate(schedule.date)}</td>
                            <td>{schedule.day}</td>
                            <td>
                              <small className="text-muted">
                                {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                              </small>
                            </td>
                            <td>
                              <span className={`badge ${schedule.postpone_status === 0 ? 'bg-success' : 'bg-warning text-dark'}`}>
                                {schedule.postpone_status === 0 ? 'Active' : 'Postponed'}
                              </span>
                            </td>
                            <td>
                              {schedule.meet_url ? (
                                <a
                                  href={schedule.meet_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`btn btn-sm ${isJoinDisabled ? 'btn-outline-secondary disabled' : 'btn-outline-primary'}`}
                                  onClick={e => isJoinDisabled && e.preventDefault()}
                                  aria-disabled={isJoinDisabled}
                                  title={isJoinDisabled ? (isPastTab ? 'Session date has passed' : 'Join will be available on the session date') : 'Join Meet'}
                                >
                                  {isPastTab ? 'Expired' : (isJoinDisabled ? 'Upcoming' : 'Join Meet')}
                                </a>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              {isPastTab ? (
                                schedule.recording_file ? (
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => openVideo(schedule.recording_file)}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="top"
                                    title={getRecordingTooltip(schedule)}
                                  >
                                    Play Record
                                  </button>
                                ) : (
                                  <span
                                    className="d-inline-block"
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="top"
                                    title={getRecordingTooltip(schedule)}
                                  >
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      disabled
                                      style={{ pointerEvents: 'none' }}
                                    >
                                      Play Record
                                    </button>
                                  </span>
                                )
                              ) : (
                                <div className="d-flex gap-2">
                                  <button 
                                    className="btn btn-sm btn-warning text-white" 
                                    onClick={() => handleEditSchedule(schedule.id)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => {
                                      setSelectedSchedule(schedule);
                                      setShowDeleteModal(true);
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </td>
                            <td>
                              {schedule.remark ? (
                                <span className="text-success">{schedule.remark}</span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="d-md-none">
                  {(activeTab === 'upcoming' ? upcomingSessions : pastSessions).map((schedule) => {
                    const isPastTab = activeTab === 'past';
                    const sessionStatus = getSessionStatus(schedule.date);
                    const isJoinDisabled = isPastTab ? true : (sessionStatus === 'upcoming');
                    return (
                      <div key={`${schedule.id}-${schedule.recordingid ?? 'nr'}`} className="card border-0 border-bottom rounded-0">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <h6 className="mb-1 fw-semibold">{schedule.title}</h6>
                              <small className="text-muted">{schedule.batch_name}</small>
                            </div>
                            <span className={`badge ${schedule.postpone_status === 0 ? 'bg-success' : 'bg-warning text-dark'}`}>
                              {schedule.postpone_status === 0 ? 'Active' : 'Postponed'}
                            </span>
                          </div>

                          <div className="row small mb-2">
                            <div className="col-6">
                              <strong>Date:</strong> {formatDate(schedule.date)}
                            </div>
                            <div className="col-6">
                              <strong>Day:</strong> {schedule.day}
                            </div>
                            <div className="col-12">
                              <strong>Time:</strong> {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                            </div>
                          </div>

                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              {schedule.meet_url ? (
                                <a
                                  href={schedule.meet_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`btn btn-sm ${isJoinDisabled ? 'btn-outline-secondary disabled' : 'btn-outline-primary'}`}
                                  onClick={e => isJoinDisabled && e.preventDefault()}
                                  aria-disabled={isJoinDisabled}
                                  title={isJoinDisabled ? (isPastTab ? 'Session date has passed' : 'Join will be available on the session date') : 'Join Meet'}
                                >
                                  {isPastTab ? 'Expired' : (isJoinDisabled ? 'Upcoming' : 'Join Meet')}
                                </a>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </div>
                            <div className="d-flex gap-2">
                              {isPastTab ? (
                                schedule.recording_file ? (
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => openVideo(schedule.recording_file)}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="top"
                                    title={getRecordingTooltip(schedule)}
                                  >
                                    Play Record
                                  </button>
                                ) : (
                                  <span
                                    className="d-inline-block"
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="top"
                                    title={getRecordingTooltip(schedule)}
                                  >
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      disabled
                                      style={{ pointerEvents: 'none' }}
                                    >
                                      Play Record
                                    </button>
                                  </span>
                                )
                              ) : (
                                <>
                                  <button 
                                    className="btn btn-sm btn-warning text-white" 
                                    onClick={() => handleEditSchedule(schedule.id)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => {
                                      setSelectedSchedule(schedule);
                                      setShowDeleteModal(true);
                                    }}
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                          {schedule.remark && (
                            <div className="mt-2">
                              <strong>Remarks:</strong> <span className="text-success">{schedule.remark}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <nav className="card-footer bg-transparent">
                    <ul className="pagination justify-content-end mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(p => p - 1)}>Previous</button>
                      </li>
                      {[...Array(totalPages)].map((_, i) => (
                        <li key={i + 1} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                      </li>
                    </ul>
                  </nav>
                )}
              </>
            ) : (
              <div className="text-center py-5">
                <i className="fas fa-calendar-alt fa-3x text-muted mb-3"></i>
                <h6 className="text-muted">
                  {courseNameFilter || dateFilter || dateRangeFilter || startDateFilter || endDateFilter
                    ? 'No schedules match your filters.'
                    : 'No schedules found for this batch.'}
                </h6>
                <button className="btn btn-primary" onClick={handleAddSchedule}>
                  <i className="fas fa-plus me-1"></i> Add Schedule
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ======== DELETE MODAL ======== */}
      {showDeleteModal && selectedSchedule && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-danger pb-3">
                <h5 className="modal-title text-white">Confirm Delete</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body text-center">
                <i className="fas fa-trash-alt fa-3x text-danger mb-3"></i>
                <p>Are you sure you want to delete this schedule?</p>
                <div className="text-start mt-3">
                  <p><strong>Batch:</strong> {selectedSchedule.batch_name}</p>
                  <p><strong>Course title:</strong> {selectedSchedule.title}</p>
                  <p><strong>Date:</strong> {formatDate(selectedSchedule.date)} ({selectedSchedule.day})</p>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={confirmDelete} disabled={isDeleting}>
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        show={showVideoModal}
        onHide={() => setShowVideoModal(false)}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title className='pb-3'>Past Recording</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div style={{ height: '400px', background: 'black' }}>
            <video
              src={videoUrl}
              controls
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              controlsList="nodownload"
              onContextMenu={(e) => e.preventDefault()}
              preload="metadata"
            />
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default IFViewSchedulePage;
